import { FiTrash2 } from "react-icons/fi";
import { AiFillFolder } from "react-icons/ai";
import { FaFileCsv, FaFileExcel } from "react-icons/fa6";
import { VscJson } from "react-icons/vsc";
import { FileSystemItem } from "./types";
import { getFileIcon } from "./utils";
import Modal from "../modal";

type BrowseDeleteModalProps = {
  show: boolean;
  selectedItems: Set<string>;
  filteredAndSortedItems: FileSystemItem[];
  deleteConfirmationChecked: boolean;
  onToggleConfirmation: (checked: boolean) => void;
  onCancel: () => void;
  onConfirm: () => void;
};

const BrowseDeleteModal = ({
  show,
  selectedItems,
  filteredAndSortedItems,
  deleteConfirmationChecked,
  onToggleConfirmation,
  onCancel,
  onConfirm,
}: BrowseDeleteModalProps) => {
  if (!show) return null;

  return (
    <Modal
      isOpen={show}
      onClose={onCancel}
      panelClassName="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col"
    >
      <div className="p-6 flex-1 overflow-y-auto">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 p-2 bg-red-100 rounded-full">
            <FiTrash2 className="w-6 h-6 text-red-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">
              Batch Delete Required
            </h3>

            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800 font-medium mb-1">
                Important: Mako currently only supports batch deletes
              </p>
              <p className="text-sm text-gray-700">
                Files uploaded together must be deleted together. By deleting
                the selected file{selectedItems.size > 1 ? "s" : ""},
                you will also delete all other files from the same upload
                batch.
              </p>
            </div>

            <div className="mt-4">
              <p className="text-sm font-medium text-gray-900 mb-2">
                Files to be deleted from this batch:
              </p>
              <div className="space-y-1 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                {Array.from(selectedItems).map((path) => {
                  const item = filteredAndSortedItems.find(
                    (i: FileSystemItem) => i.path === path
                  );
                  if (!item) return null;
                  return (
                    <div
                      key={path}
                      className="flex items-center gap-2 text-sm text-gray-900"
                    >
                      {item.type === "folder" ? (
                        <AiFillFolder className="text-yellow-500" size={16} />
                      ) : (
                        getFileIcon(item.name)
                      )}
                      <span className="font-medium">{item.name}</span>
                      <span className="text-xs text-gray-500">(selected)</span>
                    </div>
                  );
                })}
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <FaFileCsv className="text-green-500" size={16} />
                  <span>data_analysis_results.csv</span>
                  <span className="text-xs text-gray-500">(same batch)</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <VscJson className="text-yellow-500" size={16} />
                  <span>experiment_metadata.json</span>
                  <span className="text-xs text-gray-500">(same batch)</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <FaFileExcel className="text-green-600" size={16} />
                  <span>sample_measurements.xlsx</span>
                  <span className="text-xs text-gray-500">(same batch)</span>
                </div>
              </div>
            </div>

            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deleteConfirmationChecked}
                  onChange={(e) => onToggleConfirmation(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-red-600 border-gray-300 rounded focus:outline-none focus:ring-0"
                />
                <span className="text-sm text-gray-700">
                  I understand I will be deleting the selected file
                  {selectedItems.size > 1 ? "s" : ""}, as well as{" "}
                  <span className="font-medium">3 other files</span> that were
                  uploaded together in the same batch
                </span>
              </label>
            </div>

            <p className="mt-4 text-sm text-red-600 font-medium">
              This action cannot be undone.
            </p>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-3 px-6 pb-6 border-t pt-4">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={!deleteConfirmationChecked}
          className={`px-4 py-2 rounded-lg transition-colors ${
            deleteConfirmationChecked
              ? "bg-red-600 text-white hover:bg-red-700"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          Delete Entire Batch ({selectedItems.size + 3} Files)
        </button>
      </div>
    </Modal>
  );
};

export default BrowseDeleteModal;
